import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, TrendingDown, ArrowRight, Zap } from 'lucide-react';

const COMPARISONS = [
  { metric: { en: 'Average FPS', de: 'Durchschnittl. FPS' }, before: 120, after: 240, unit: 'FPS', higher: true },
  { metric: { en: '1% Low FPS', de: '1% Low FPS' }, before: 62, after: 155, unit: 'FPS', higher: true },
  { metric: { en: 'Input Lag', de: 'Input-Latenz' }, before: 28, after: 9, unit: 'ms', higher: false },
  { metric: { en: 'CPU Load', de: 'CPU-Auslastung' }, before: 78, after: 41, unit: '%', higher: false },
];

function AnimatedNumber({ value, unit, color, inView }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-end gap-1"
    >
      <span className="font-outfit text-5xl font-light" style={{ color }}>{value}</span>
      <span className="font-inter text-lg text-zinc-600 mb-1">{unit}</span>
    </motion.div>
  );
}

export default function FPSSection() {
  const { lang } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="performance" className="py-40 relative overflow-hidden" ref={ref}>
      <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/25 to-transparent origin-top"
      />
      <motion.div animate={{ opacity: [0.02, 0.06, 0.02] }} transition={{ duration: 7, repeat: Infinity }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 65% 45% at 50% 55%, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-inter text-xs tracking-[0.3em] text-zinc-700 uppercase mb-4">Real Results</p>
          <h2 className="font-outfit text-4xl sm:text-5xl font-light tracking-tight text-white mb-4">
            {lang === 'de' ? 'Echte Performance-Gains' : 'Real Performance Gains'}
          </h2>
          <p className="font-inter text-zinc-500 text-base max-w-md mx-auto">
            {lang === 'de' ? 'Gemessen auf echten Systemen. Reproduzierbar, verifiziert.' : 'Measured on real systems. Reproducible, verified.'}
          </p>
        </motion.div>

        {/* Big stat highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {[
            { icon: TrendingUp, val: '+100%', label: lang === 'de' ? 'Mehr FPS' : 'More FPS', sub: 'avg. result' },
            { icon: TrendingDown, val: '-68%', label: lang === 'de' ? 'Weniger Lag' : 'Less Input Lag', sub: 'ms reduced' },
            { icon: TrendingDown, val: '-47%', label: lang === 'de' ? 'CPU-Last' : 'CPU Load', sub: 'efficiency up' },
            { icon: TrendingUp, val: '+150%', label: lang === 'de' ? '1% Lows' : '1% Low FPS', sub: 'no stutters' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5, boxShadow: '0 0 35px rgba(255,255,255,0.07)' }}
                className="flex flex-col items-center p-6 rounded-2xl border border-white/10 backdrop-blur-xl transition-all duration-300"
                style={{ background: 'rgba(255,255,255,0.025)' }}
              >
                <motion.span initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1, type: 'spring', stiffness: 200 }}
                  className="font-outfit text-3xl sm:text-4xl font-light text-white mb-1"
                >{s.val}</motion.span>
                <span className="font-inter text-sm text-zinc-400 font-medium text-center">{s.label}</span>
                <span className="font-inter text-xs text-zinc-700 mt-1">{s.sub}</span>
              </motion.div>
            );
          })}
        </div>

        {/* Before / After comparison cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* BEFORE */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="p-8 rounded-3xl border border-white/8 backdrop-blur-xl"
            style={{ background: 'rgba(255,255,255,0.015)' }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-2.5 h-2.5 rounded-full bg-white/25" />
              <span className="font-outfit text-base tracking-widest text-zinc-600 uppercase">
                {lang === 'de' ? 'Vorher' : 'Before'}
              </span>
            </div>
            <div className="space-y-6">
              {COMPARISONS.map((c, i) => (
                <div key={i} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-inter text-xs text-zinc-600">{c.metric[lang] || c.metric.en}</span>
                    <span className="font-inter text-sm text-zinc-500">{c.before}{c.unit}</span>
                  </div>
                  <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }}
                      animate={inView ? { width: `${(c.before / (c.higher ? c.after * 1.1 : c.before * 1.1)) * 100}%` } : {}}
                      transition={{ duration: 1, delay: i * 0.1 }}
                      className="h-full rounded-full bg-white/20"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Arrow (mobile: hidden) */}
          <div className="hidden md:absolute md:flex items-center justify-center" style={{ left: '50%', top: '50%', transform: 'translate(-50%, 0)', zIndex: 20 }}>
            <motion.div animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
              className="w-10 h-10 rounded-full border border-white/20 bg-white/5 flex items-center justify-center"
            >
              <ArrowRight size={16} className="text-white" />
            </motion.div>
          </div>

          {/* AFTER */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative p-8 rounded-3xl border border-white/22 backdrop-blur-xl"
            style={{ background: 'rgba(255,255,255,0.04)', boxShadow: '0 0 50px rgba(255,255,255,0.06)' }}
          >
            <div className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 55%)' }}
            />
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-white" />
                <span className="font-outfit text-base tracking-widest text-white uppercase">
                  {lang === 'de' ? 'Nachher (V2)' : 'After (V2)'}
                </span>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-inter text-green-400 bg-green-400/10 rounded-full px-3 py-1">
                <Zap size={10} /> Vanta V2
              </span>
            </div>
            <div className="space-y-6">
              {COMPARISONS.map((c, i) => {
                const pct = c.higher
                  ? Math.round(((c.after - c.before) / c.before) * 100)
                  : Math.round(((c.before - c.after) / c.before) * 100);
                const Icon = c.higher ? TrendingUp : TrendingDown;
                return (
                  <div key={i} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-inter text-xs text-zinc-400">{c.metric[lang] || c.metric.en}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-inter text-sm text-white font-medium">{c.after}{c.unit}</span>
                        <span className="flex items-center gap-0.5 text-xs font-inter text-green-400">
                          <Icon size={10} /> {c.higher ? '+' : '-'}{pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }}
                        animate={inView ? { width: `${(c.after / (c.higher ? c.after * 1.1 : c.before * 1.1)) * 100}%` } : {}}
                        transition={{ duration: 1.2, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                        className="h-full rounded-full bg-white/90"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Arrow */}
        <div className="flex justify-center my-6">
          <motion.div animate={{ x: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
            className="w-12 h-12 rounded-full border border-white/18 bg-white/[0.05] backdrop-blur-xl flex items-center justify-center"
          >
            <ArrowRight size={18} className="text-white" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
